import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Sparkles } from 'lucide-react';
import { usePhrases } from '@/context/PhrasesContext';
import { useLanguage } from '@/context/LanguageContext';
import { getCategoryColor } from '@/utils/categoryColors';

// Storage key for tracking shown phrases
const POTD_HISTORY_KEY = 'clearcomm-potd-history';
const MAX_HISTORY_SIZE = 100; // Track last 100 phrases to avoid repetition

export const PhraseOfTheDay = () => {
  const navigate = useNavigate();
  const { phrases, categories } = usePhrases();
  const { t, isSpanish, translatePhraseContent, translateSingleCategory } = useLanguage();
  const [translatedPhrase, setTranslatedPhrase] = useState(null);
  const [translatedCategory, setTranslatedCategory] = useState(null);

  // Get phrase history from localStorage
  const getPhraseHistory = () => {
    try {
      const history = localStorage.getItem(POTD_HISTORY_KEY);
      return history ? JSON.parse(history) : { lastDate: null, shownIds: [], currentId: null };
    } catch {
      return { lastDate: null, shownIds: [], currentId: null };
    }
  };

  // Save phrase history to localStorage
  const savePhraseHistory = (history) => {
    try {
      // Keep only last MAX_HISTORY_SIZE entries
      if (history.shownIds.length > MAX_HISTORY_SIZE) {
        history.shownIds = history.shownIds.slice(-MAX_HISTORY_SIZE);
      }
      localStorage.setItem(POTD_HISTORY_KEY, JSON.stringify(history));
    } catch {
      // Ignore storage errors
    }
  };

  // Get a truly random phrase that hasn't been shown recently
  // Changes daily and avoids repetition
  const phraseOfDay = useMemo(() => {
    if (!phrases || phrases.length === 0) return null;
    
    const today = new Date();
    const dateString = `${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()}`;
    const history = getPhraseHistory();
    
    // If we already selected a phrase today, return it
    if (history.lastDate === dateString && history.currentId) {
      const existingPhrase = phrases.find(p => p.id === history.currentId);
      if (existingPhrase) return existingPhrase;
    }
    
    // Need to select a new phrase for today
    // Filter out recently shown phrases
    const availablePhrases = phrases.filter(p => !history.shownIds.includes(p.id));
    
    // If we've shown all phrases, reset history but keep current selection
    const candidatePhrases = availablePhrases.length > 0 ? availablePhrases : phrases;
    
    // Use date + phrase count as seed for consistent selection across users
    // but also incorporate randomness to ensure variety
    let hash = 0;
    const seedString = `${dateString}-${phrases.length}`;
    for (let i = 0; i < seedString.length; i++) {
      const char = seedString.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    
    // Multiple hash rounds for better distribution
    hash = Math.abs(hash);
    hash = (hash * 2654435761) % (2 ** 32); // Knuth's multiplicative hash
    
    const index = hash % candidatePhrases.length;
    const selectedPhrase = candidatePhrases[index];
    
    // Update history
    const newHistory = {
      lastDate: dateString,
      shownIds: [...history.shownIds.filter(id => id !== selectedPhrase.id), selectedPhrase.id],
      currentId: selectedPhrase.id
    };
    
    // Reset if we've cycled through all phrases
    if (availablePhrases.length === 0) {
      newHistory.shownIds = [selectedPhrase.id];
    }
    
    savePhraseHistory(newHistory);
    
    return selectedPhrase;
  }, [phrases]);

  // Translate phrase when language changes
  useEffect(() => {
    const doTranslate = async () => {
      if (phraseOfDay && isSpanish) {
        const translated = await translatePhraseContent(phraseOfDay);
        setTranslatedPhrase(translated);
        
        // Also translate category
        const cat = categories.find(c => c.id === phraseOfDay.category);
        if (cat) {
          const translatedCat = await translateSingleCategory(cat);
          setTranslatedCategory(translatedCat);
        }
      } else {
        setTranslatedPhrase(null);
        setTranslatedCategory(null);
      }
    };
    doTranslate();
  }, [phraseOfDay, isSpanish, translatePhraseContent, translateSingleCategory, categories]);

  const handleViewPhrase = () => {
    if (phraseOfDay) {
      navigate(`/phrase/${phraseOfDay.id}`);
    }
  };

  if (!phraseOfDay) return null;

  const displayPhrase = isSpanish && translatedPhrase ? translatedPhrase : phraseOfDay;
  const category = categories.find(c => c.id === phraseOfDay.category);
  const displayCategory = isSpanish && translatedCategory ? translatedCategory : category;
  const colors = getCategoryColor(phraseOfDay.category);

  return (
    <div className="container px-4 py-2 sm:py-6">
      <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-accent/5 overflow-hidden relative">
        {/* Decorative sparkles */}
        <div className="absolute top-4 right-4 opacity-20">
          <Sparkles className="h-16 w-16 text-primary" />
        </div>
        
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg sm:text-xl font-bold text-primary">
              {isSpanish ? 'Frase del Día' : 'Phrase of the Day'}
            </CardTitle>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {/* Category badge */}
          <Badge className={`${colors.bg} ${colors.text} text-xs`}>
            {displayCategory?.name || 'General'}
          </Badge>
          
          {/* Phrase */}
          <h3 className="text-2xl sm:text-3xl font-bold tracking-tight">
            "{phraseOfDay.phrase}"
          </h3>
          
          {/* Meaning preview */}
          <div className="space-y-2">
            <p className="text-sm font-medium text-muted-foreground">
              {isSpanish ? 'Significado Real:' : 'Actual Meaning:'}
            </p>
            <p className="text-base text-foreground/80 line-clamp-2">
              {displayPhrase.actualMeaning}
            </p>
          </div>
          
          {/* View more button */}
          <Button 
            onClick={handleViewPhrase}
            className="mt-2"
            variant="default"
          >
            {isSpanish ? 'Ver Definición Completa' : 'View Full Definition'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default PhraseOfTheDay;
